import { Component } from 'react';
import { connect } from 'react-redux';
import { Col, Input } from 'reactstrap';
import * as actions from '../../../actions';
import RcSlider from 'rc-slider';
import { getTranslationKey } from '../../../modules/utils/helpers';
import { SwitchItem } from '../../../components';
import 'rc-slider/assets/index.css';
import type { Notify } from '../../../typings/redux';
import type { Watermark, WatermarkPosition } from './types';

const MIN_SCALE_PERCENT = 10;
const MIN_OPACITY_PERCENT = 1;

const fonts = [
  { value: 'FONT_SANS_32_BLACK', label: 'Open Sans, 32px, schwarz' },
  { value: 'FONT_SANS_64_BLACK', label: 'Open Sans, 64px, schwarz' },
  { value: 'FONT_SANS_128_BLACK', label: 'Open Sans, 128px, schwarz' },
  { value: 'FONT_SANS_32_WHITE', label: 'Open Sans, 32px, weiß' },
  { value: 'FONT_SANS_64_WHITE', label: 'Open Sans, 64px, weiß' },
  { value: 'FONT_SANS_128_WHITE', label: 'Open Sans, 128px, weiß' }
];

interface EditWatermarkDataProps {
  watermark: Watermark;
  updateWatermark: (state: any, notify: Notify) => Promise<boolean>;
  updateWatermarkValue: (data: { watermark_id: string; key: string; value: any }) => void;
  notify: Notify;
}

interface EditWatermarkDataState {
  noPositionSelected: boolean;
}

class EditWatermarkData extends Component<EditWatermarkDataProps, EditWatermarkDataState> {
  constructor(props: EditWatermarkDataProps) {
    super(props);

    this.state = {
      noPositionSelected: false
    };
    this.updateWatermark = this.updateWatermark.bind(this);
    this.updateSelectedPosition = this.updateSelectedPosition.bind(this);
  }

  componentDidMount() {
    this.enforceMinimumValues();
  }

  componentDidUpdate() {
    this.enforceMinimumValues();
  }

  enforceMinimumValues() {
    const { watermark } = this.props;
    if (watermark.type !== 'image') return;

    if ((watermark.scale || 0) * 100 < MIN_SCALE_PERCENT) {
      this.props.updateWatermarkValue({
        watermark_id: watermark._id,
        key: 'scale',
        value: MIN_SCALE_PERCENT / 100
      });
    }

    if ((watermark.opacity || 0) * 100 < MIN_OPACITY_PERCENT) {
      this.props.updateWatermarkValue({
        watermark_id: watermark._id,
        key: 'opacity',
        value: MIN_OPACITY_PERCENT / 100
      });
    }
  }

  updateWatermark() {
    this.props.updateWatermark(this.state, this.props.notify);
  }

  updateSelectedPosition(selectedPosition: WatermarkPosition) {
    this.setState({ noPositionSelected: false });
    let positions = [...this.props.watermark.positions];

    if (positions.includes(selectedPosition)) {
      positions = positions.filter((p) => p !== selectedPosition);
    } else {
      positions.push(selectedPosition);
    }

    if (!positions.length) {
      this.setState({ noPositionSelected: true });
      positions.push('Center');
    }

    this.props.updateWatermarkValue({
      watermark_id: this.props.watermark._id,
      key: 'positions',
      value: positions
    });
  }

  render() {
    const { watermark } = this.props;
    if (!watermark) return null;

    const scalePercent = (watermark.scale || 0) * 100;
    const displayedScalePercent = Math.max(scalePercent, MIN_SCALE_PERCENT);
    const opacityPercent = (watermark.opacity || 0) * 100;
    const displayedOpacityPercent = Math.max(opacityPercent, MIN_OPACITY_PERCENT);

    return (
      <Col md="6" sm="12" className="border-left">
        <small>
          <b>{getTranslationKey('watermark.description')}</b>
        </small>
        <Input
          className="mb-4"
          value={watermark.description}
          type="text"
          disabled={watermark?.isDefault}
          onChange={({ target: { value } }) =>
            this.props.updateWatermarkValue({
              watermark_id: watermark._id,
              key: 'description',
              value
            })
          }
        />

        {watermark.type === 'text' && (
          <div>
            <small>
              <b>{getTranslationKey('watermark.text')}</b>
            </small>
            <Input
              className="mb-4"
              value={watermark.text}
              type="text"
              disabled={watermark?.isDefault}
              onChange={({ target: { value } }) =>
                this.props.updateWatermarkValue({
                  watermark_id: watermark._id,
                  key: 'text',
                  value
                })
              }
            />
          </div>
        )}

        {watermark.type === 'image' ? (
          <div>
            <small>
              <b>{getTranslationKey('watermark.scale')}</b>
              <b className="float-right">{parseInt(String(displayedScalePercent))}%</b>
            </small>
            {/* @ts-ignore */}
            <RcSlider
              className="mb-4"
              min={MIN_SCALE_PERCENT}
              max={100}
              value={displayedScalePercent}
              onChange={(value) =>
                this.props.updateWatermarkValue({
                  watermark_id: watermark._id,
                  key: 'scale',
                  value: Math.max(value as number, MIN_SCALE_PERCENT) / 100
                })
              }
            />

            <small>
              <b>{getTranslationKey('watermark.opacity')}</b>
              <b className="float-right">{parseInt(String(displayedOpacityPercent))}%</b>
            </small>
            {/* @ts-ignore */}
            <RcSlider
              className="mb-4"
              min={MIN_OPACITY_PERCENT}
              max={100}
              value={displayedOpacityPercent}
              onChange={(value) =>
                this.props.updateWatermarkValue({
                  watermark_id: watermark._id,
                  key: 'opacity',
                  value: Math.max(value as number, MIN_OPACITY_PERCENT) / 100
                })
              }
            />
          </div>
        ) : (
          <div>
            <small>
              <b>{getTranslationKey('watermark.font')}</b>
            </small>
            <div>
              <Input
                onChange={({ target: { value } }) =>
                  this.props.updateWatermarkValue({
                    watermark_id: watermark._id,
                    key: 'jimpFont',
                    value
                  })
                }
                selected={watermark.jimpFont}
                defaultValue={watermark.jimpFont}
                disabled={watermark?.isDefault}
                className="mb-4"
                type="select"
              >
                {fonts.map((font, key) => (
                  <option key={key} value={font.value}>
                    {font.label}
                  </option>
                ))}
              </Input>
            </div>
          </div>
        )}
        <small>
          <b>{getTranslationKey('watermark.positions')}</b>
        </small>
        <table className="watermark-positions-table">
          <tbody>
            <tr>
              <td
                className={watermark.positions.includes('NorthWest') ? 'active' : ''}
                onClick={() => this.updateSelectedPosition('NorthWest')}
                aria-label="North West position"
              />
              <td className="not-allowed" aria-label="Not available" />
              <td
                className={watermark.positions.includes('NorthEast') ? 'active' : ''}
                onClick={() => this.updateSelectedPosition('NorthEast')}
                aria-label="North East position"
              />
            </tr>
            <tr>
              <td className="not-allowed" aria-label="Not available" />
              <td
                className={watermark.positions.includes('Center') ? 'active' : ''}
                onClick={() => this.updateSelectedPosition('Center')}
                aria-label="Center position"
              />
              <td className="not-allowed" aria-label="Not available" />
            </tr>
            <tr>
              <td
                className={watermark.positions.includes('SouthWest') ? 'active' : ''}
                onClick={() => this.updateSelectedPosition('SouthWest')}
                aria-label="South West position"
              />
              <td className="not-allowed" aria-label="Not available" />
              <td
                className={watermark.positions.includes('SouthEast') ? 'active' : ''}
                onClick={() => this.updateSelectedPosition('SouthEast')}
                aria-label="South East position"
              />
            </tr>
          </tbody>
        </table>

        {this.state.noPositionSelected && (
          <small>{getTranslationKey('watermark.noPosition')}</small>
        )}

        <div className="mt-4">
          <SwitchItem
            id={`aiWatermarkProtector_${watermark._id}`}
            value={!!watermark.useAiWatermarkProtector}
            label={getTranslationKey('watermark.useAiWatermarkProtector')}
            direction="row"
            labelMargin="10px"
            withTooltip
            isIconTooltip
            tooltipText={getTranslationKey('watermark.useAiWatermarkProtectorHint')}
            changeCallback={(checked) => {
              // Local only, like every other field here: the preview re-renders
              // from this value and Save persists it. Regenerating the existing
              // images must not start before the user saved the modal.
              this.props.updateWatermarkValue({
                watermark_id: watermark._id,
                key: 'useAiWatermarkProtector',
                value: checked
              });
            }}
          />
        </div>
      </Col>
    );
  }
}

export default connect(null, actions)(EditWatermarkData);
